/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { memo } from "react";
import type { IconProps } from "./types";

/** Circle download arrow icon */
export const CircleDownloadIcon: React.FC<IconProps> = memo(
  ({ width = "15", height = "15", className }) => (
    <svg
      aria-hidden="true"
      focusable="false"
      height={height}
      width={width}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      className={className}
    >
      <path
        fill="currentColor"
        d="M256 464a208 208 0 1 1 0-416 208 208 0 1 1 0 416zM256 0a256 256 0 1 0 0 512A256 256 0 1 0 256 0zM376.9 294.6c4.5-4.2 7.1-10.1 7.1-16.3c0-12.3-10-22.3-22.3-22.3H304V160c0-17.7-14.3-32-32-32l-32 0c-17.7 0-32 14.3-32 32v96H150.3C138 256 128 266 128 278.3c0 6.2 2.6 12.1 7.1 16.3l107.1 99.9c3.8 3.5 8.7 5.5 13.8 5.5s10.1-2 13.8-5.5l107.1-99.9z"
      />
    </svg>
  ),
);
CircleDownloadIcon.displayName = "CircleDownloadIcon";

/** Search magnifying glass icon */
export const SearchIcon: React.FC<IconProps> = memo(
  ({ width = "22", height = "22", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 22 22"
      fill="none"
      className={className}
    >
      <path
        d="M19.8667 21.5L12.5167 14.15C11.9333 14.6167 11.2625 14.9861 10.5042 15.2583C9.74583 15.5306 8.93889 15.6667 8.08333 15.6667C5.96389 15.6667 4.17014 14.9326 2.70208 13.4646C1.23403 11.9965 0.5 10.2028 0.5 8.08333C0.5 5.96389 1.23403 4.17014 2.70208 2.70208C4.17014 1.23403 5.96389 0.5 8.08333 0.5C10.2028 0.5 11.9965 1.23403 13.4646 2.70208C14.9326 4.17014 15.6667 5.96389 15.6667 8.08333C15.6667 8.93889 15.5306 9.74583 15.2583 10.5042C14.9861 11.2625 14.6167 11.9333 14.15 12.5167L21.5 19.8667L19.8667 21.5ZM8.08333 13.3333C9.54167 13.3333 10.7812 12.8229 11.8021 11.8021C12.8229 10.7812 13.3333 9.54167 13.3333 8.08333C13.3333 6.625 12.8229 5.38542 11.8021 4.36458C10.7812 3.34375 9.54167 2.83333 8.08333 2.83333C6.625 2.83333 5.38542 3.34375 4.36458 4.36458C3.34375 5.38542 2.83333 6.625 2.83333 8.08333C2.83333 9.54167 3.34375 10.7812 4.36458 11.8021C5.38542 12.8229 6.625 13.3333 8.08333 13.3333Z"
        fill="#838383"
      />
    </svg>
  ),
);
SearchIcon.displayName = "SearchIcon";

/** Circle X clear/close icon — used in search inputs */
export const CircleClearIcon: React.FC<IconProps> = memo(
  ({ width = "20", height = "20", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="red"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M10 1.25C5.125 1.25 1.25 5.125 1.25 10C1.25 14.875 5.125 18.75 10 18.75C14.875 18.75 18.75 14.875 18.75 10C18.75 5.125 14.875 1.25 10 1.25ZM10 17.5C5.875 17.5 2.5 14.125 2.5 10C2.5 5.875 5.875 2.5 10 2.5C14.125 2.5 17.5 5.875 17.5 10C17.5 14.125 14.125 17.5 10 17.5Z"
        fill="#2c2c2c"
      />
      <path
        d="M13.375 14.375L10 11L6.625 14.375L5.625 13.375L9 10L5.625 6.625L6.625 5.625L10 9L13.375 5.625L14.375 6.625L11 10L14.375 13.375L13.375 14.375Z"
        fill="#2c2c2c"
      />
    </svg>
  ),
);
CircleClearIcon.displayName = "CircleClearIcon";

/** Simple download arrow with tray */
export const DownloadTrayIcon: React.FC<IconProps> = memo(
  ({ width = "24", height = "24", fill = "white", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.979 19.804 4.587 19.412C4.195 19.02 3.99933 18.5493 4 18V15H6V18H18V15H20V18C20 18.55 19.804 19.021 19.412 19.413C19.02 19.805 18.5493 20.0007 18 20H6Z"
        fill={fill}
      />
    </svg>
  ),
);
DownloadTrayIcon.displayName = "DownloadTrayIcon";

/** Windows logo icon */
export const WindowsLogoIcon: React.FC<IconProps> = memo(
  ({
    width = "20",
    height = "20",
    fill = "#ffffff",
    stroke = "#ffffff",
    className,
  }) => (
    <svg
      fill={fill}
      height={height}
      width={width}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 497.89 497.89"
      xmlSpace="preserve"
      stroke={stroke}
      className={className}
    >
      <g>
        <g>
          <g>
            <polygon points="227.959,39.869 227.959,242.386 496.549,242.386 496.549,0" />
            <polygon points="1.336,244.746 211.172,244.746 211.172,41.818 1.336,72.798" />
            <polygon points="227.959,458.017 496.549,497.886 496.549,261.535 227.959,261.535" />
            <polygon points="1.336,425.086 211.172,456.066 211.172,261.531 1.336,261.531" />
          </g>
        </g>
      </g>
    </svg>
  ),
);
WindowsLogoIcon.displayName = "WindowsLogoIcon";

/** Shopping cart icon for buy now buttons */
export const ShoppingCartIcon: React.FC<IconProps> = memo(
  ({
    width = "20",
    height = "20",
    fill = "#fff",
    stroke = "#fff",
    className,
  }) => (
    <svg
      fill={fill}
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 902.86 902.86"
      xmlSpace="preserve"
      stroke={stroke}
      strokeWidth="0.009028600000000001"
      transform="rotate(0)"
      className={className}
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0" />
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth="30.697240000000004"
      />
      <g id="SVGRepo_iconCarrier">
        <g>
          <g>
            <path d="M671.504,577.829l110.485-432.609H902.86v-68H729.174L703.128,179.2L0,178.697l74.753,399.129h596.751V577.829z M685.766,247.188l-67.077,262.64H131.199L81.928,246.756L685.766,247.188z" />{" "}
            <path d="M578.418,825.641c59.961,0,108.743-48.783,108.743-108.744s-48.782-108.742-108.743-108.742H168.717 c-59.961,0-108.744,48.781-108.744,108.742s48.782,108.744,108.744,108.744c59.962,0,108.743-48.783,108.743-108.744 c0-14.4-2.821-28.152-7.927-40.742h208.069c-5.107,12.59-7.928,26.342-7.928,40.742 C469.675,776.858,518.457,825.641,578.418,825.641z M209.46,716.897c0,22.467-18.277,40.744-40.743,40.744 c-22.466,0-40.744-18.277-40.744-40.744c0-22.465,18.277-40.742,40.744-40.742C191.183,676.155,209.46,694.432,209.46,716.897z M619.162,716.897c0,22.467-18.277,40.744-40.743,40.744s-40.743-18.277-40.743-40.744c0-22.465,18.277-40.742,40.743-40.742 S619.162,694.432,619.162,716.897z" />
          </g>
        </g>
      </g>
    </svg>
  ),
);
ShoppingCartIcon.displayName = "ShoppingCartIcon";

/** Document/file icon — used for PDF previews */
export const DocumentIcon: React.FC<IconProps> = memo(
  ({ width = "40", height = "40", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
        stroke="#7C7C7C"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2V8H20"
        stroke="#7C7C7C"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
);
DocumentIcon.displayName = "DocumentIcon";

/** X close button for remove/dismiss actions */
export const CloseXIcon: React.FC<IconProps> = memo(
  ({ width = "40", height = "40", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="5 5 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M23.8258 10.1749L10.1758 23.8249M10.1758 10.1749L23.8258 23.8249"
        stroke="black"
        strokeWidth="2.1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
);
CloseXIcon.displayName = "CloseXIcon";

/** Minimize/collapse icon for modals */
export const MinimizeIcon: React.FC<IconProps> = memo(
  ({ width = "35", height = "35", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M6 14H10M10 14V18M10 14L4 20M18 10H14M14 10V6M14 10L20 4"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
);
MinimizeIcon.displayName = "MinimizeIcon";

/*
 * T-7: window controls for the chat panel.
 *
 * These are new siblings rather than edits to CloseXIcon and MinimizeIcon,
 * because those two are shared with ScreenshotSection.tsx, which renders both
 * with no size props and depends on their present defaults and artwork. In the
 * chat panel "minimise" means hide the window and keep the launcher, and its
 * glyph is a horizontal bar; in the screenshot modal the same icon means shrink
 * this back down, and the collapse-arrows glyph is the right one. One component
 * cannot mean both.
 */

/**
 * P-3 (Session 376): the four window controls share a canvas, a stroke weight
 * and a default size.
 *
 * Sixteen units on a sixteen-pixel default, so one unit is exactly one screen
 * pixel and a one-unit line lands on a whole pixel rather than straddling two
 * of them. Keeping the twenty-pixel default while moving to a sixteen-unit
 * canvas — which is what the report proposed — scales every unit by 1.25, so a
 * line centred on unit 8 would cover screen pixels 9.375 to 10.625 and the
 * browser would draw it as two grey half-lines. That is the opposite of the
 * crispness the item asks for. The buttons are 30px square, so a 16px glyph
 * still sits comfortably inside them and matches the weight of a native window
 * control.
 */
const WINDOW_CONTROL_VIEWBOX = "0 0 16 16";
const WINDOW_CONTROL_STROKE = 1;

const windowControlSvgProps = (
  // IconProps allows a number as well as a string for both dimensions, and the
  // SVG element accepts either. Matching that here rather than narrowing it
  // keeps these four components callable exactly like every other icon.
  width: string | number,
  height: string | number,
  className?: string,
) => ({
  width,
  height,
  viewBox: WINDOW_CONTROL_VIEWBOX,
  fill: "none" as const,
  xmlns: "http://www.w3.org/2000/svg",
  className,
  "aria-hidden": "true" as const,
  focusable: "false" as const,
});

/** Window close — white on the blue chat header, inherits via currentColor. */
export const WindowCloseIcon: React.FC<IconProps> = memo(
  ({ width = "16", height = "16", className }) => (
    <svg {...windowControlSvgProps(width, height, className)}>
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth={WINDOW_CONTROL_STROKE}
        strokeLinecap="round"
      />
    </svg>
  ),
);
WindowCloseIcon.displayName = "WindowCloseIcon";

/** Window minimise — a horizontal dash through the icon centre. */
export const WindowMinimizeIcon: React.FC<IconProps> = memo(
  ({ width = "16", height = "16", className }) => (
    <svg {...windowControlSvgProps(width, height, className)}>
      <path
        d="M3 8h10"
        stroke="currentColor"
        strokeWidth={WINDOW_CONTROL_STROKE}
        strokeLinecap="round"
      />
    </svg>
  ),
);
WindowMinimizeIcon.displayName = "WindowMinimizeIcon";

/** Upload arrow icon */
export const UploadIcon: React.FC<IconProps> = memo(
  ({ width = "20", height = "20", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5"
        stroke="#7C7C7C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.1667 6.66667L10 2.5L5.83334 6.66667"
        stroke="#7C7C7C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 2.5V12.5"
        stroke="#7C7C7C"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
);
UploadIcon.displayName = "UploadIcon";

/**
 * Window maximise — a single square window.
 *
 * P-3 correction 3: renamed from `MaximizeIcon`, not aliased. The report kept
 * the old names as aliases "for callers outside the chat panel"; there are
 * none. Both names appeared only in their own definitions, the icon index, the
 * chat panel and two test files. Two names for one drawing is how a pair drifts
 * apart later.
 */
export const WindowMaximizeIcon: React.FC<IconProps> = memo(
  ({ width = "16", height = "16", className }) => (
    <svg {...windowControlSvgProps(width, height, className)}>
      <rect
        x="3.5"
        y="3.5"
        width="9"
        height="9"
        rx="1"
        stroke="currentColor"
        strokeWidth={WINDOW_CONTROL_STROKE}
      />
    </svg>
  ),
);
WindowMaximizeIcon.displayName = "WindowMaximizeIcon";

/**
 * Window restore — a front window with the visible edges of the one behind it.
 *
 * P-3: the previous glyph drew two complete squares with `fill="none"`, so the
 * back square's lines ran straight through the front one — eight visible
 * crossings in a twenty-pixel box, which reads as a scribble rather than as two
 * windows. Drawing only the top and right edges of the back window is the
 * standard picture, and it is what every desktop system draws: a window behind
 * another window is partly hidden. Do NOT "fix" this by closing the back square
 * again; a complete back square is the fault, not the remedy.
 */
export const WindowRestoreIcon: React.FC<IconProps> = memo(
  ({ width = "16", height = "16", className }) => (
    <svg {...windowControlSvgProps(width, height, className)}>
      {/* The two visible edges of the window behind, meeting at its corner. */}
      <path
        d="M5.5 4.5h6v6"
        stroke="currentColor"
        strokeWidth={WINDOW_CONTROL_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* The front window, drawn last so it sits over the edges above. */}
      <rect
        x="3.5"
        y="6.5"
        width="6"
        height="6"
        rx="1"
        fill="none"
        stroke="currentColor"
        strokeWidth={WINDOW_CONTROL_STROKE}
      />
    </svg>
  ),
);
WindowRestoreIcon.displayName = "WindowRestoreIcon";

/**
 * Support bot mark. Decorative: every control that contains it carries its
 * own accessible name, so exposing the icon as well would announce twice.
 *
 * V-8 item 14: moved here from inside TechSupportChat.tsx, where it was a
 * local component and could not be reused. The inline nudge on the technical
 * support form renders the same mark; without the move the two copies would
 * drift the first time either was adjusted.
 */
/**
 * P-1 (Session 393) — the approved Deskshare Support mark.
 *
 * The artwork supplied with this session is vector, and it is drawn here rather
 * than shipped as an image file, for four reasons in order of what they cost if
 * ignored:
 *
 *   1. An <img> cannot inherit colour. The nudge on the technical support form
 *      is coloured by `.ai-chat-nudge-icon { color: #2c5aa0; }`, which works
 *      only because the mark fills with currentColor. An image turns that rule
 *      into dead CSS that still looks live.
 *   2. A file under public/assets/img/ depends on the still-open operations
 *      item that /assets/ must be served by this deployment on every
 *      environment. An inline shape carries no such dependency.
 *   3. One file changed instead of four, and no extra request for a 500-byte
 *      image. A 24px raster would also need 48px and 72px copies and a srcset
 *      per surface to stay sharp on the 2x and 3x screens most phones have.
 *   4. There is no moment on first paint where the control is drawn and the
 *      mark is not.
 *
 * The artwork has two colours and they are inverted between surfaces: a white
 * body with dark eyes on the launcher and the panel header, a blue body with
 * white eyes on the support-form nudge. The body follows currentColor; the eyes
 * take `--ds-bot-eye`, which each surface sets to its own background colour so
 * they read as cut-outs. That keeps every colour decision in the stylesheets,
 * where the rest of this project keeps them, and it is why no variant prop is
 * needed — the two supplied files are the same seven shapes with the two
 * colours swapped.
 *
 * One value was changed deliberately: the supplied launcher file draws the eyes
 * in #1D1D4E, one step in each channel from the navigation navy #1E1E4F. The
 * difference is below the threshold at which two colours can be told apart at
 * all, let alone at the size of a 3px dot, and keeping it would put a second,
 * slightly different copy of the brand navy into the code base for nobody to
 * keep in step. The eyes are the colour of the surface behind them.
 */
export const BotIcon: React.FC<IconProps> = memo(
  ({ width = 24, height = 24, className }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      width={width}
      height={height}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {/* Body — follows the colour of the control the mark sits in. */}
      <rect x="11" y="3" width="2" height="4" fill="currentColor" />
      <circle cx="12" cy="3" r="2" fill="currentColor" />
      <rect x="4" y="7" width="16" height="13" rx="4" fill="currentColor" />
      <rect x="2" y="11" width="2" height="5" rx="1" fill="currentColor" />
      <rect x="20" y="11" width="2" height="5" rx="1" fill="currentColor" />
      {/* Eyes — cut out in the colour of the surface behind the mark. */}
      <circle
        cx="8.5"
        cy="12.5"
        r="1.5"
        style={{ fill: "var(--ds-bot-eye, #1E1E4F)" }}
      />
      <circle
        cx="15.5"
        cy="12.5"
        r="1.5"
        style={{ fill: "var(--ds-bot-eye, #1E1E4F)" }}
      />
    </svg>
  ),
);
BotIcon.displayName = "BotIcon";
